import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import Input from '../components/Input';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import studentPortalService from '../services/studentPortal.service';
import type { SignupCountry } from '../types/studentPortal';
import CountryFlag from '../components/CountryFlag';

import LogoImage from '../assets/1.png';

export default function Register() {
  const { t, language } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    country_id: '',
  });
  const [countries, setCountries] = useState<SignupCountry[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesError, setCountriesError] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    studentPortalService.getSignupCountries()
      .then((items) => {
        setCountries(items);
        if (items.length === 1) setFormData((current) => ({ ...current, country_id: String(items[0].id) }));
      })
      .catch(() => setCountriesError(true))
      .finally(() => setCountriesLoading(false));
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t.auth.errors.requiredField;
    }

    if (!formData.email) {
      newErrors.email = t.auth.errors.requiredField;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.auth.errors.invalidEmail;
    }

    if (!formData.password) {
      newErrors.password = t.auth.errors.requiredField;
    } else if (formData.password.length < 8) {
      newErrors.password = t.auth.errors.weakPassword;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t.auth.errors.requiredField;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t.auth.errors.passwordMismatch;
    }

    if (!formData.country_id) newErrors.country_id = languageCopy.required;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await register({ name: formData.name, email: formData.email, password: formData.password, country_id: Number(formData.country_id) });
      
      // Registration successful - redirect to email verification page
      navigate('/verify-email', { state: { email: formData.email, fromRegistration: true } });
    } catch (error: unknown) {
      console.error('Registration error:', error);

      if (axios.isAxiosError(error) && error.response?.status === 400) {
        setErrors({ form: t.auth.errors.emailExists });
      } else {
        setErrors({ form: t.auth.errors.networkError });
      }
    } finally {
      setLoading(false);
    }
  };

  const languageCopy = language === 'ar' ? {
    required: 'يرجى اختيار وجهتك الدراسية.', destination: 'أين تريد الدراسة؟',
    available: 'التقديم والعقد ورحلة التأشيرة متاحة الآن.', loading: 'جارٍ تحميل الوجهات المتاحة…',
    unavailable: 'تعذر تحميل الوجهات. حدّث الصفحة وحاول مرة أخرى.',
  } : {
    required: 'Please choose your study destination.', destination: 'Where do you want to study?',
    available: 'Applications, contract and visa journey are available now.', loading: 'Loading available destinations…',
    unavailable: 'Destinations could not be loaded. Please refresh and try again.',
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F4EEFA] via-white to-[#E8F8F5] relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid Background */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundSize: '40px 40px',
            backgroundImage: 'linear-gradient(to right, #e4e4e7 1px, transparent 1px), linear-gradient(to bottom, #e4e4e7 1px, transparent 1px)',
          }}
        />

        {/* Floating Shapes */}
        <div className="absolute top-20 right-10 w-20 h-20 bg-gradient-to-br from-[#B18AE0] to-[#5B3FD0] rounded-full opacity-20"></div>
        <div className="absolute top-40 left-20 w-16 h-16 bg-[#9FFFCB] opacity-20 rounded-xl rotate-45"></div>
        <div className="absolute bottom-32 right-20 w-14 h-14 bg-[#5B3FD0] rounded-full opacity-15"></div>
        <div className="absolute bottom-20 left-32 w-12 h-12 bg-gradient-to-br from-[#B18AE0] to-[#9FFFCB] opacity-20 rounded-lg rotate-12"></div>
        <div className="absolute top-1/2 left-10 w-10 h-10 bg-[#9FFFCB] rounded-full opacity-25"></div>
      </div>

      <div className="max-w-lg w-full space-y-8 relative z-10">
        {/* Logo/Brand */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center gap-3 group mb-8">
            <img src={LogoImage} alt="EduNasser Logo" className="w-32 h-32 object-contain" />
          </Link>

          <h2 className="text-4xl font-bold">
            <span className="text-[#2C2C2C]">{t.auth.register.title}</span>
          </h2>
          <p className="mt-2 text-gray-600">
            {t.auth.register.subtitle}
          </p>
        </div>

        {/* Register Form Card */}
        <form
          className="mt-8 bg-white/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/50 space-y-6"
          onSubmit={handleSubmit}
        >
          {/* Decorative top border */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#B18AE0] via-[#5B3FD0] to-[#9FFFCB] rounded-t-3xl"></div>

          {errors.form && (
            <div className="bg-red-50/80 backdrop-blur-sm border border-red-400 text-red-700 px-4 py-3 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {errors.form}
              </div>
            </div>
          )}

          <Input
            label={t.auth.register.name}
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            required
            autoComplete="name"
            disabled={loading}
          />

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">{languageCopy.destination}</label>
            {countriesLoading ? (
              <div className="rounded-2xl border border-[#e7e0f2] bg-[#faf8fd] p-4 text-sm text-gray-500">{languageCopy.loading}</div>
            ) : countriesError || countries.length === 0 ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{languageCopy.unavailable}</div>
            ) : (
              <div className="grid gap-3">
                {countries.map((country) => (
                  <button key={country.id} type="button" disabled={loading} onClick={() => setFormData((current) => ({...current, country_id:String(country.id)}))}
                    className={`flex items-center gap-4 rounded-2xl border p-4 text-left transition ${formData.country_id === String(country.id) ? 'border-[#5B3FD0] bg-[#F1EDFF] ring-2 ring-[#5B3FD0]/10' : 'border-[#e7e0f2] bg-white hover:border-[#B18AE0]'}`}>
                    <span className="grid h-12 w-12 place-items-center overflow-hidden rounded-xl bg-white text-2xl shadow-sm">
                      <CountryFlag code={country.code} fallback={country.country_flag} label={country.name} size="large" />
                    </span>
                    <span className="flex-1"><strong className="block text-[#211a31]">{country.name}</strong><small className="text-gray-500">{languageCopy.available}</small></span>
                    <span className={`h-5 w-5 rounded-full border-4 ${formData.country_id === String(country.id) ? 'border-[#5B3FD0] bg-white' : 'border-gray-200'}`} />
                  </button>
                ))}
              </div>
            )}
            {errors.country_id && <p className="text-sm text-red-600">{errors.country_id}</p>}
          </div>

          <Input
            label={t.auth.register.email}
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            required
            autoComplete="email"
            disabled={loading}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t.auth.register.password}
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              required
              autoComplete="new-password"
              disabled={loading}
            />

            <Input
              label={t.auth.register.confirmPassword}
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              required
              autoComplete="new-password"
              disabled={loading}
            />
          </div>

          <Button
            type="submit"
            disabled={loading || countriesLoading || countriesError || countries.length === 0}
            className="w-full shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                <span>{t.common.loading}</span>
              </div>
            ) : (
              <>
                {t.auth.register.button}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </>
            )}
          </Button>

          <div className="text-center pt-4">
            <p className="text-sm text-gray-600">
              {t.auth.register.hasAccount}{' '}
              <Link
                to="/login"
                className="text-[#5B3FD0] hover:text-[#B18AE0] font-bold"
              >
                {t.auth.register.loginLink}
              </Link>
            </p>
          </div>
        </form>

        {/* Back to Home Link */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#707070] hover:text-[#5B3FD0] font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
